import { Loader2 } from "lucide-react";
import { Button, ButtonLabel, buttonVariants } from "@patient-watch/ui/button";
import { DialogClose } from "@patient-watch/ui/dialog";
import {
    cloneElement,
    isValidElement,
    useState,
    type ComponentProps,
    type MouseEvent,
    type ReactElement,
    type ReactNode,
} from "react";

import ActionButtonIcon, { type ActionButtonIconType } from "./ActionButtonIcon";
import { cn } from "@patient-watch/ui/utils";

type BaseActionButtonProps = {
    disabled?: boolean;
    label?: string;
    isLoading?: boolean;
    loadingText?: string;
    icon?: ReactNode;
    iconType?: ActionButtonIconType;
    /** Icon before label (Back). Default is after label (Next, Save, etc.). */
    iconPosition?: "leading" | "trailing";
    testId?: string;
    buttonType?: "submit" | "button";
    variant?: ComponentProps<typeof Button>["variant"];
    isDialog?: boolean;
    children?: ReactNode;
} & Omit<ComponentProps<typeof Button>, "children" | "type" | "variant">;

const BaseActionButton = ({
    disabled,
    label,
    isLoading = false,
    loadingText,
    icon,
    iconType,
    iconPosition = "trailing",
    testId,
    buttonType = "submit",
    variant,
    isDialog = false,
    className,
    size = "lg",
    asChild = false,
    children: slottableChild,
    ...buttonProps
}: BaseActionButtonProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const isDisabled = disabled || isLoading;

    const renderIcon = () => {
        if (isLoading) return <Loader2 className="w-4 h-4 animate-spin shrink-0" />;

        if (iconType) {
            return (
                <span className="shrink-0">
                    <ActionButtonIcon type={iconType} isHovered={isHovered} />
                </span>
            );
        }

        return icon ? <span className="shrink-0">{icon}</span> : null;
    };

    const labelContent = isLoading ? loadingText : label;
    const iconNode = renderIcon();
    const content = labelContent ? (
        <ButtonLabel icon={iconNode} iconPosition={isLoading ? "leading" : iconPosition}>
            {labelContent}
        </ButtonLabel>
    ) : (
        iconNode
    );

    const sharedClassName = cn("flex justify-center items-center w-full transition-all duration-300", className);

    const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
        setIsHovered(true);
        buttonProps.onMouseEnter?.(event as MouseEvent<HTMLButtonElement>);
    };

    const handleMouseLeave = (event: MouseEvent<HTMLElement>) => {
        setIsHovered(false);
        buttonProps.onMouseLeave?.(event as MouseEvent<HTMLButtonElement>);
    };

    const slottableElement =
        asChild && isValidElement(slottableChild)
            ? (slottableChild as ReactElement<{
                  className?: string;
                  onMouseEnter?: (event: MouseEvent<HTMLElement>) => void;
                  onMouseLeave?: (event: MouseEvent<HTMLElement>) => void;
              }>)
            : null;

    const button = slottableElement ? (
        cloneElement(slottableElement, {
            ...buttonProps,
            "aria-disabled": isDisabled || undefined,
            "data-testid": testId,
            className: cn(
                buttonVariants({ variant, size }),
                sharedClassName,
                slottableElement.props.className,
                isDisabled && "pointer-events-none opacity-40"
            ),
            onMouseEnter: (event: MouseEvent<HTMLElement>) => {
                handleMouseEnter(event);
                slottableElement.props.onMouseEnter?.(event);
            },
            onMouseLeave: (event: MouseEvent<HTMLElement>) => {
                handleMouseLeave(event);
                slottableElement.props.onMouseLeave?.(event);
            },
            children: content,
        } as Record<string, unknown>)
    ) : (
        <Button
            disabled={isDisabled}
            size={size}
            type={buttonType}
            variant={variant}
            data-testid={testId}
            className={sharedClassName}
            {...buttonProps}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {content}
        </Button>
    );

    if (isDialog) {
        return <DialogClose asChild>{button}</DialogClose>;
    }
    return button;
};

export default BaseActionButton;
